import React from 'react';
import { Link } from 'react-router-dom';
import FlowRCMLogo from '../components/FlowRCMLogo';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200">
        <div className="flex justify-center py-6">
          <Link to="/login" className="inline-block">
            <FlowRCMLogo variant="light" hideSubtitle={true} />
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
          <p className="text-slate-600 mb-8">Last Updated: January 8, 2026</p>

          <div className="space-y-6 text-slate-700">
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing or using FlowRCM ("the Service"), you agree to be bound by these Terms of Service 
                and our Privacy Policy. If you do not agree to these terms, you must not use the Service. 
                These terms constitute a legally binding agreement between you (the dental practice) and FlowRCM.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Description of Service</h2>
              <p>
                FlowRCM provides AI-powered revenue cycle management software for dental practices, including:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Insurance benefits verification and eligibility checking</li>
                <li>Automated pre-authorization and prior authorization processing</li>
                <li>Claim validation and submission</li>
                <li>Denial management and appeals processing</li>
                <li>Revenue recovery and collections optimization</li>
                <li>Reporting and analytics</li>
              </ul>
              <p className="mt-2">
                The Service is provided as software-as-a-service (SaaS) and accessed via web browser.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">3. User Accounts and Registration</h2>
              <p className="mb-2">
                To use the Service, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security and confidentiality of your login credentials</li>
                <li>Accept responsibility for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized access or security breach</li>
                <li>Ensure all users in your practice comply with these Terms</li>
              </ul>
              <p className="mt-2">
                You may not share your account credentials or allow unauthorized access to your account.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">4. HIPAA Compliance and Business Associate Agreement</h2>
              <p>
                FlowRCM is HIPAA-compliant and operates as a Business Associate under the Health Insurance 
                Portability and Accountability Act (HIPAA). By using the Service:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>You represent that you are a HIPAA-covered entity or operate under one</li>
                <li>You agree to execute a Business Associate Agreement (BAA) with FlowRCM</li>
                <li>You will use the Service only for permitted purposes under HIPAA</li>
                <li>You will implement appropriate safeguards for Protected Health Information (PHI)</li>
              </ul>
              <p className="mt-2">
                We maintain administrative, physical, and technical safeguards to protect PHI as required by HIPAA.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Acceptable Use Policy</h2>
              <p className="mb-2">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Use the Service for any unlawful purpose or in violation of these Terms</li>
                <li>Attempt to gain unauthorized access to the Service or related systems</li>
                <li>Interfere with, disrupt, or impose unreasonable load on the Service</li>
                <li>Share patient data inappropriately or in violation of HIPAA</li>
                <li>Reverse engineer, decompile, or attempt to extract source code</li>
                <li>Remove or obscure any proprietary notices or labels</li>
                <li>Use the Service to transmit malware, viruses, or harmful code</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
                <li>Sublicense, resell, or distribute the Service to third parties</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Payment Terms and Billing</h2>
              <p>
                Subscription fees are billed in advance on a monthly or annual basis, as selected during registration.
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Payment Authorization:</strong> By providing payment information, you authorize FlowRCM to charge the applicable fees to your payment method</li>
                <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless cancelled before the renewal date</li>
                <li><strong>Price Changes:</strong> We may change pricing with 30 days' notice. Continued use after notice constitutes acceptance</li>
                <li><strong>Late Payment:</strong> Failure to pay may result in service suspension or termination</li>
                <li><strong>Taxes:</strong> Fees do not include applicable sales tax, VAT, or other taxes, which you are responsible for</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Refund Policy</h2>
              <p>
                Subscription fees are non-refundable except as required by law or as follows:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>If you cancel within the first 7 days of your initial subscription, you may receive a full refund</li>
                <li>No refunds are provided for partial months or unused portions of annual subscriptions</li>
                <li>Fees for completed workflow executions are non-refundable</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Data Ownership and License</h2>
              <p className="mb-2">
                <strong>Your Data:</strong> You retain all ownership rights to your practice data and patient information. 
                You grant FlowRCM a limited license to use this data solely to provide and improve the Service.
              </p>
              <p className="mt-2">
                <strong>Our Content:</strong> FlowRCM retains all rights to the Service, including software, algorithms, 
                documentation, and all intellectual property. You receive a limited, non-exclusive, non-transferable 
                license to use the Service during your subscription period.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Service Availability and Support</h2>
              <p>
                We strive to provide 99.5% uptime for the Service, but do not guarantee uninterrupted availability.
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Maintenance:</strong> We may perform scheduled maintenance with advance notice</li>
                <li><strong>Downtime:</strong> We are not liable for service interruptions due to circumstances beyond our control</li>
                <li><strong>Support:</strong> Email support is available at info@flowrcmdental.com during business hours</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Limitation of Liability</h2>
              <p className="mb-2">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>FLOWRCM SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID IN THE 12 MONTHS PRECEDING THE CLAIM</li>
                <li>WE ARE NOT LIABLE FOR LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES</li>
                <li>WE ARE NOT LIABLE FOR CLAIMS PROCESSING ERRORS, DENIED CLAIMS, OR PAYER DECISIONS</li>
              </ul>
              <p className="mt-2">
                Some jurisdictions do not allow limitation of certain damages, so these limitations may not apply to you.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Warranty Disclaimer</h2>
              <p>
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Warranties of merchantability or fitness for a particular purpose</li>
                <li>Warranties of non-infringement</li>
                <li>Warranties regarding accuracy, reliability, or results</li>
                <li>Warranties that the Service will be error-free or secure</li>
              </ul>
              <p className="mt-2">
                You acknowledge that AI-powered results are probabilistic and should be reviewed by qualified personnel.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">12. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless FlowRCM, its affiliates, and their respective officers, 
                directors, employees, and agents from any claims, damages, losses, or expenses (including attorney fees) 
                arising from:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Your use or misuse of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Your violation of HIPAA or other applicable laws</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">13. Termination</h2>
              <p className="mb-2">
                <strong>By You:</strong> You may cancel your subscription at any time through your account settings. 
                Cancellation takes effect at the end of the current billing period.
              </p>
              <p className="mt-2">
                <strong>By Us:</strong> We may suspend or terminate your account immediately if you:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Violate these Terms or our Acceptable Use Policy</li>
                <li>Fail to pay fees when due</li>
                <li>Engage in fraudulent or illegal activity</li>
                <li>Pose a security risk to the Service or other users</li>
              </ul>
              <p className="mt-2">
                <strong>Upon Termination:</strong> You will lose access to the Service. You may request a data 
                export within 30 days. After 30 days, your data will be securely deleted.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">14. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify you of material changes 
                via email at least 30 days before they take effect. Your continued use of the Service after 
                changes take effect constitutes acceptance of the modified Terms. If you do not agree with the 
                changes, you must cancel your subscription.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">15. Governing Law and Dispute Resolution</h2>
              <p>
                These Terms shall be governed by the laws of the State of Michigan, United States, without 
                regard to conflict of law provisions. Any disputes arising from these Terms or the Service 
                shall be resolved through binding arbitration in Michigan, except that either party may seek 
                injunctive relief in court for intellectual property claims.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">16. Miscellaneous</h2>
              <p>
                <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy and BAA, 
                constitute the entire agreement between you and FlowRCM.
              </p>
              <p className="mt-2">
                <strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in effect.
              </p>
              <p className="mt-2">
                <strong>No Waiver:</strong> Our failure to enforce any right does not constitute a waiver of that right.
              </p>
              <p className="mt-2">
                <strong>Assignment:</strong> You may not assign these Terms without our consent. We may assign 
                these Terms in connection with a merger or acquisition.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">17. Contact Information</h2>
              <p>
                For questions about these Terms of Service:<br /><br />
                Email: support@flowrcmdental.com
              </p>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <Link to="/login" className="text-teal-600 hover:text-teal-700 font-semibold">
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;